import router from '@core/router';
import store from '@core/store';
import { Auth } from '@core/auth';

import { uuid } from '@utils/string';

import Organisation from '@/models/Organisation';

/**
 * Go to a new route
 * @param {Object,String} route
 */
export const goto = async route => {
    await router.push(route);
};

export const goBack = (force = false) => {
    if (force) {
        window.history.back();
    } else {
        if (store.getters['routerHistory/hasHistory']) {
            router.back();
        } else {
            router.push({ name: 'Dashboard' });
        }
    }
};

export const isPublic = route => {
    if (route && typeof route === 'object' && route.meta) {
        return route.meta.public === true;
    }

    return false;
};

export const resolveRoute = async name => {
    try {
        return await router.resolve({ name });
    } catch (error) {
        return null;
    }
};

export const pageErrorRedirect = (title = null, message = null, next = null) => {
    let nextRoute = { name: 'PageError', params: {} };

    if (title) {
        nextRoute.params.title = title;
    }

    if (message) {
        nextRoute.params.message = message;
    }

    if (next && typeof next === 'function') {
        return next(nextRoute);
    }

    return router.push(nextRoute);
};

export function findRouteByName(name, routes) {
    routes = routes || router.options.routes;

    for (const route of routes) {
        if (route.name === name) {
            return route;
        }
        if (route.children) {
            const found = findRouteByName(name, route.children);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

export function userHasRoutePermissions(route) {
    const permissions = route?.permissions || route?.meta?.permissions;

    if (!permissions) {
        return true;
    }

    const can = Auth().can;

    return permissions.every(permission => can(permission));
}

export function userHasRouteOrganisations(route) {
    const organisations = route?.organisations || route?.meta?.organisations;

    if (!organisations) {
        return true;
    }

    const organisationType = Auth().organisation_type;

    if (!organisationType) {
        return false;
    }

    return organisations.some(type => type === organisationType);
}

export function userHasRouteAccess(route) {
    const access = route?.access || route?.meta?.access;

    if (!access) {
        return true;
    }

    if (typeof access === 'boolean') {
        return access;
    }

    if (typeof access === 'function') {
        let organisation = Auth().organisation;

        if (!(organisation instanceof Organisation)) {
            organisation = new Organisation(organisation);
        }

        return access(organisation);
    }

    return true;
}

export const getMenu = async (menu, level = 0) => {
    try {
        if (typeof menu === 'string') {
            menu = (await import(`@core/router/menus/${menu}.js`)).default;
        }
    } catch (error) {
        console.error('Cannot find menu:', `@core/router/menus/${menu}.js`, error);
        menu = [];
    }

    // Remove the unauthorised items
    let items = menu.filter(item => {
        if (!userHasRoutePermissions(item)) {
            return false;
        }

        if (!userHasRouteOrganisations(item)) {
            return false;
        }

        if (!userHasRouteAccess(item)) {
            return false;
        }

        return true;
    });

    for (const itemIndex in items) {
        let item = items[`${itemIndex}`];

        if ('children' in item && item.children.length) {
            item.hasChildren = true;
            item.children = await getMenu(item.children, level + 1);
        }

        if ('getChildren' in item) {
            item.hasChildren = true;
        }

        item.active = false;

        if (!item.href && !('path' in item)) {
            const route = await resolveRoute(item.route);
            if (route) {
                item.path = route.route.path
                    .split('/')
                    .filter(part => {
                        return !part.includes('?');
                    })
                    .join('/');
            }
        }

        if (!('id' in item)) {
            item.id = uuid();
        }

        item.level = level;

        items[`${itemIndex}`] = item;
    }

    items = items.filter(item => {
        if (item.hasChildren && !item.children.length) {
            return false;
        }

        return true;
    });

    return items;
};
